import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { RegisterSplitLayout } from '../../components/register/RegisterSplitLayout';
import { SidebarPreview } from '../../components/register/SidebarPreview';
import { useRegistrationOnboardingGuard } from '../../lib/hooks/useRegistrationOnboardingGuard';
import { trackRegisterStepViewed } from '../../lib/analytics/onboardingEvents';
import {
  ONBOARDING_AREAS,
  computeExpandedSections,
  EXPANDED_MENUS_STORAGE_KEY,
} from '../../lib/config/onboardingModules';

/** Step 8: aree di interesse a chip + anteprima live della sidebar.
 *  "Continua" con selezione → scrive localStorage['expanded-menus'];
 *  "Salta" o selezione vuota → nessuna scrittura (default attuale). */
export function ModulesPage() {
  const navigate = useNavigate();
  const { ready } = useRegistrationOnboardingGuard();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => { trackRegisterStepViewed('modules'); }, []);

  const expandedIds = useMemo(
    () => new Set(computeExpandedSections(Array.from(selected))),
    [selected],
  );

  const toggle = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const goNext = () => navigate('/register/team');

  const handleContinue = () => {
    const sections = computeExpandedSections(Array.from(selected));
    if (sections.length > 0) {
      try {
        localStorage.setItem(EXPANDED_MENUS_STORAGE_KEY, JSON.stringify(sections));
      } catch (e) {
        console.warn('[Modules] impossibile salvare expanded-menus:', e);
      }
    }
    goNext();
  };

  if (!ready) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#787774]" />
      </div>
    );
  }

  return (
    <RegisterSplitLayout visual={<SidebarPreview expandedIds={expandedIds} />} step={8}>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#37352F] mb-1">Cosa userai in Flusia?</h1>
        <p className="text-[#787774] text-sm">
          Seleziona le aree che ti interessano: il menu si adatta. Potrai cambiare tutto in ogni momento.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {ONBOARDING_AREAS.map(({ key, label }) => {
          const active = selected.has(key);
          return (
            <button key={key} type="button" onClick={() => toggle(key)}
              className={`px-3.5 py-2 rounded-full text-sm border transition-all ${
                active
                  ? 'border-[#2383E2] bg-[rgba(35,131,226,0.06)] text-[#2383E2] font-medium'
                  : 'border-[rgba(55,53,47,0.16)] text-[#37352F] hover:border-[rgba(55,53,47,0.3)] hover:bg-[rgba(55,53,47,0.03)]'
              }`}>
              {label}
            </button>
          );
        })}
      </div>
      <div className="mt-8 space-y-3">
        <button type="button" onClick={handleContinue} className="notion-register-btn w-full">
          <span>Continua</span>
          <ArrowRight className="w-4 h-4" />
        </button>
        <button type="button" onClick={goNext}
          className="w-full text-center text-sm text-[#787774] hover:text-[#37352F] transition-colors py-1.5">
          Salta
        </button>
      </div>
    </RegisterSplitLayout>
  );
}
